import dayjs from 'dayjs';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { formatPhoneNumberDisplay } from '../../../../util/formatting';
import { PropertyCartItem, PropertyAccess } from '../types/cart.types';
import { AccessType } from '../model/CartModel';

type AccessCellProps = {
  access: PropertyAccess | null;
  item: PropertyCartItem;
  onEdit?: (item: PropertyCartItem) => void;
  readOnly?: boolean;
};

const renderAccessCodeRow = (access: PropertyAccess) => {
  if (access.accessType === AccessType.LOCKBOX) {
    return (
      <Typography variant="body">
        {access.accessCode}, {access.lockboxLocation}
      </Typography>
    );
  }
  if (access.accessType === AccessType.KEYPAD) {
    return <Typography variant="body">{access.accessCode || ''}</Typography>;
  }
  if (access.accessType === AccessType.RENTLY) {
    return (
      <Typography variant="body">
        {access.rentlyRegistrationPin || ''}
      </Typography>
    );
  }
  return null;
};

const AccessCell = ({
  access,
  item,
  onEdit,
  readOnly = false,
}: AccessCellProps) => {
  if (!access) {
    return <Typography variant="body">-</Typography>;
  }

  const accessTypeRow = renderAccessCodeRow(access);
  const editLabel = `Edit appointment info for ${item.formattedAddress || 'property'}`;

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex gap-4">
          <Typography variant="body-semibold">Onsite contact:</Typography>
          <Typography variant="body">{access.onsiteContactName}</Typography>
        </div>
        <div className="flex gap-4">
          <Typography variant="body-semibold">Contact phone:</Typography>
          <Typography variant="body">
            {formatPhoneNumberDisplay(access.onsiteContactPhone)}
          </Typography>
        </div>
        {accessTypeRow && (
          <div className="flex gap-4">
            <Typography variant="body-semibold">{access.accessType}:</Typography>
            {accessTypeRow}
          </div>
        )}
        {item.firstAvailableDate && (
          <div className="flex gap-4">
            <Typography variant="body-semibold">First available date:</Typography>
            <Typography variant="body">
              {dayjs(item.firstAvailableDate).format('MMMM D, YYYY')}
            </Typography>
          </div>
        )}
        {item.specialInstructions && (
          <div className="flex flex-col gap-0">
            <Typography variant="body-semibold">Additional notes:</Typography>
            <Typography variant="body">{item.specialInstructions}</Typography>
          </div>
        )}
      </div>
      {!readOnly && onEdit && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-muted-foreground hover:text-primary"
          aria-label={editLabel}
          onClick={() => onEdit(item)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default AccessCell;
